import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";
import { toast } from "sonner";

import { AppShell } from "@/components/app/app-shell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { kitsQuery, programsAuthedQuery } from "@/lib/queries";
import { adminSaveKit } from "@/lib/admin.functions";
import { reportHandledSync } from "@/lib/observability";

export const Route = createFileRoute("/_authenticated/t1dwpanel/kits")({
  head: () => ({
    meta: [
      { title: "Kit Editor | Warrior Volunteer" },
      { name: "description", content: "Kit Editor in the T1D Warriors volunteer workspace." },
      { property: "og:title", content: "Kit Editor | Warrior Volunteer" },
      { property: "og:description", content: "Kit Editor in the T1D Warriors volunteer workspace." },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: KitEditorPage,
});

function KitEditorPage() {
  const { data: kits = [] } = useQuery(kitsQuery);
  const { data: programs = [] } = useQuery(programsAuthedQuery);
  const save = useServerFn(adminSaveKit);
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [activeId, setActiveId] = useState<string | null>(null);

  async function handleSave(event: React.FormEvent<HTMLFormElement>, id?: string) {
    event.preventDefault();
    const formEl = event.currentTarget;
    const form = new FormData(formEl);
    setBusy(true);
    try {
      await save({
        data: {
          id,
          program_id: String(form.get("program_id") ?? ""),
          title: String(form.get("title") ?? ""),
          intro: String(form.get("intro") ?? ""),
          email_subject: String(form.get("email_subject") ?? ""),
          email_body: String(form.get("email_body") ?? ""),
          call_script: String(form.get("call_script") ?? ""),
          call_tips: String(form.get("call_tips") ?? ""),
          text_message: String(form.get("text_body_0") ?? ""),
          text_messages: [0, 1, 2, 3, 4]
            .map((i) => ({
              label: String(form.get(`text_label_${i}`) ?? ""),
              body: String(form.get(`text_body_${i}`) ?? ""),
            }))
            .filter((t) => t.body.trim().length > 0),

          one_pager_url: String(form.get("one_pager_url") ?? ""),

          landing_url: String(form.get("landing_url") ?? ""),
          is_published: form.get("is_published") === "on",
        },
      });
      toast.success(id ? "Kit updated." : "Kit created.");
      if (!id) formEl.reset();
      queryClient.invalidateQueries({ queryKey: ["kits"] });
    } catch (error) {
      reportHandledSync(error, { feature: "admin", action: "handleSave" });
      toast.error(error instanceof Error ? error.message : "Could not save the kit.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <AppShell eyebrow="Admin" title="Kit editor">
      <p className="t-body mb-6 max-w-2xl text-sm">
        Volunteers see these kits read only. Merge fields available in the email template:
        contact_first_name, organization, volunteer_name, volunteer_email.
      </p>

      <div className="space-y-4">
        {kits.map((kit) => {
          const open = activeId === kit.id;
          return (
            <div key={kit.id} className="panel">
              <button
                type="button"
                onClick={() => setActiveId(open ? null : kit.id)}
                className="flex w-full items-center justify-between px-6 py-5 text-left"
              >
                <div>
                  <p className="t-eyebrow">{kit.programs?.name}</p>
                  <h2 className="t-h3 mt-1.5 text-ice">{kit.title}</h2>
                </div>
                <span className="t-meta">{open ? "Close" : "Edit"}</span>
              </button>

              {open ? (
                <form
                  onSubmit={(e) => handleSave(e, kit.id)}
                  className="space-y-4 border-t border-border px-6 py-6"
                >
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="space-y-2">
                      <Label>Program</Label>
                      <select
                        name="program_id"
                        defaultValue={kit.program_id}
                        className="h-10 w-full rounded-sm border border-input bg-background px-3 text-sm text-ice"
                      >
                        {programs.map((program) => (
                          <option key={program.id} value={program.id}>
                            {program.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="space-y-2">
                      <Label>Title</Label>
                      <Input name="title" defaultValue={kit.title} />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>How to use this</Label>
                    <Textarea name="intro" rows={3} defaultValue={kit.intro} />
                  </div>
                  <div className="space-y-2">
                    <Label>Email subject</Label>
                    <Input name="email_subject" defaultValue={kit.email_subject} />
                  </div>
                  <div className="space-y-2">
                    <Label>Email body</Label>
                    <Textarea name="email_body" rows={14} defaultValue={kit.email_body} />
                  </div>
                  <div className="space-y-2">
                    <Label>Call script</Label>
                    <Textarea
                      name="call_script"
                      rows={10}
                      placeholder="What to say when you get them on the phone."
                      defaultValue={(kit as { call_script?: string }).call_script ?? ""}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Call tips</Label>
                    <Textarea
                      name="call_tips"
                      rows={4}
                      placeholder="Objection handling, best times to call, voicemail advice."
                      defaultValue={(kit as { call_tips?: string }).call_tips ?? ""}
                    />
                  </div>
                  <TextTemplatesEditor
                    initial={
                      ((kit as { text_messages?: { label?: string; body?: string }[] })
                        .text_messages ?? []).length
                        ? ((kit as { text_messages?: { label?: string; body?: string }[] })
                            .text_messages as { label?: string; body?: string }[])
                        : [
                            {
                              label: "First touch",
                              body: (kit as { text_message?: string }).text_message ?? "",
                            },
                          ]
                    }
                  />

                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="space-y-2">

                      <Label>One pager URL</Label>
                      <Input name="one_pager_url" defaultValue={kit.one_pager_url ?? ""} />
                    </div>
                    <div className="space-y-2">
                      <Label>Program page URL</Label>
                      <Input name="landing_url" defaultValue={kit.landing_url ?? ""} />
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <label className="flex items-center gap-2 text-sm text-ice/85">
                      <input type="checkbox" name="is_published" defaultChecked={kit.is_published} />{" "}
                      Published
                    </label>
                    <Button type="submit" disabled={busy} >
                      Save kit
                    </Button>
                  </div>
                </form>
              ) : null}
            </div>
          );
        })}
      </div>
    </AppShell>
  );
}

function TextTemplatesEditor({
  initial,
}: {
  initial: { label?: string; body?: string }[];
}) {
  const [rows, setRows] = useState(() =>
    (initial.length ? initial : [{ label: "First touch", body: "" }]).slice(0, 5),
  );

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <Label>Text messages (up to 5)</Label>
        {rows.length < 5 ? (
          <button
            type="button"
            onClick={() => setRows([...rows, { label: "", body: "" }])}
            className="text-sm font-semibold text-cyan"
          >
            Add template
          </button>
        ) : null}
      </div>
      <p className="t-meta">
        Volunteers pick one of these to text. Keep each under about 300 characters.
      </p>
      {rows.map((row, i) => (
        <div key={i} className="space-y-2 rounded-sm border border-border p-3">
          <div className="flex items-center gap-2">
            <Input
              name={`text_label_${i}`}
              value={row.label ?? ""}
              onChange={(e) =>
                setRows(rows.map((r, index) => (index === i ? { ...r, label: e.target.value } : r)))
              }
              placeholder={`Template ${i + 1} name (e.g. Follow up)`}
            />
            {rows.length > 1 ? (
              <button
                type="button"
                onClick={() => setRows(rows.filter((_, index) => index !== i))}
                className="shrink-0 text-sm font-semibold text-ice/60 hover:text-cyan"
              >
                Remove
              </button>
            ) : null}
          </div>
          <Textarea
            name={`text_body_${i}`}
            rows={4}
            value={row.body ?? ""}
            onChange={(e) =>
              setRows(rows.map((r, index) => (index === i ? { ...r, body: e.target.value } : r)))
            }
            placeholder="Short SMS version of the ask."
          />
        </div>
      ))}
    </div>
  );
}
